<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { mixed, object, string } from 'yup';

const { tenant, aiProductImageAttachment } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    aiProductImageAttachment: {
        type: Object,
        required: false,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const form = reactive({
    name: aiProductImageAttachment ? aiProductImageAttachment.name : '',
    image: aiProductImageAttachment ? aiProductImageAttachment.image : null,
    description: aiProductImageAttachment
        ? aiProductImageAttachment.description
        : '',
});

const validationSchema = object({
    name: string().required('Le nom est requis'),
    image: aiProductImageAttachment
        ? mixed().nullable()
        : mixed().required("L'image est requise"),
    description: string().nullable(),
});

function submit() {
    isSubmitting.value = true;

    let data = new FormData();
    data.append('name', form.name);
    data.append('description', form.description ?? '');

    const image = document.getElementById('image');

    if (image && image.files && image.files.length > 0) {
        data.append('image', image.files[0]);
    }

    axios
        .post(
            route('front.projectManagement.ai.postProductImageAttachment', {
                tenant: tenant.id,
                ...(aiProductImageAttachment
                    ? {
                          aiProductImageAttachment: aiProductImageAttachment.id,
                      }
                    : {}),
            }),
            data,
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.ai.productImages', {
                    tenant: tenant.id,
                    tab: 'templates',
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.ai.productImages', {
                    tenant: tenant.id,
                }),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <InputField
                    v-model="form.name"
                    label="Nom"
                    field-name="name"
                    field-id="name"
                    required
                />

                <InputField
                    v-model="form.image"
                    label="Image"
                    field-name="image"
                    field-id="image"
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    required
                />

                <TextareaField
                    v-model="form.description"
                    label="Description"
                    field-name="Description"
                    field-id="Description"
                    resize="vertical"
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="aiProductImageAttachment"
                    :url="
                        route(
                            'front.projectManagement.ai.deleteProductImageAttachment',
                            {
                                tenant: tenant.id,
                                aiProductImageAttachment:
                                    aiProductImageAttachment.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.ai.productImages',
                                    {
                                        tenant: tenant.id,
                                        tab: 'templates',
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
